import React, { useEffect } from "react";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { useFocusCanvasOnClose } from "@/lib/focusCanvas";
import { showNotificationGlobal } from "stores/useNotificationStore";

export interface TypeChangeColumn {
  column_name: string;
  from: string;
  to: string;
}

/** A question a press on Commit raised. */
export interface TypeChangeConfirm {
  view_id: string;
  table_id: string;
  columns: TypeChangeColumn[];
}

/** `from` and `to` arrive formatted by `SemanticType`'s `Display`, so they read
 *  as the column header prints them. */
function typeChangeCopy(columns: readonly TypeChangeColumn[]) {
  if (columns.length === 0) return null;

  if (columns.length === 1) {
    const { column_name, from, to } = columns[0];
    return {
      title: `Change "${column_name}" to ${to}?`,
      message: `Saving these edits changes the column from ${from} to ${to} for everyone in this model.`,
      confirmText: `Change to ${to}`,
      cancelText: `Keep as ${from}`,
    };
  }

  return {
    title: `Change ${columns.length} columns?`,
    message: (
      <>
        <p>
          Saving these edits changes {columns.length} column types for everyone
          in this model.
        </p>
        <ul className="mt-2 list-disc pl-5">
          {columns.map((c) => (
            <li key={c.column_name}>
              {`"${c.column_name}" — ${c.from} ▶ ${c.to}`}
            </li>
          ))}
        </ul>
      </>
    ),
    confirmText: `Change ${columns.length} columns`,
    cancelText: "Keep them as they are",
  };
}

const close = () => useBoundStore.getState().setTypeChangeConfirm(null);

/// The same question whether ↑ or the bar's Commit asked. × and Escape only
/// dismiss it, leaving the edits staged.
export const TypeChangeConfirmDialog: React.FC = () => {
  const confirm = useBoundStore((state) => state.typeChangeConfirm);
  const clientApp = useBoundStore((state) => state.clientApp);
  const copy = confirm ? typeChangeCopy(confirm.columns) : null;
  const stillAsks = useBoundStore(
    (state) =>
      !confirm || !!state.commitByTable[confirm.table_id]?.typeChangeStaged,
  );

  // A commit discarded or applied elsewhere takes its question with it.
  useEffect(() => {
    if (confirm && !stillAsks) close();
  }, [confirm, stillAsks]);
  useFocusCanvasOnClose(confirm !== null);

  // A conflict leaves the commit staged: say so, as the bar's Commit does.
  const answer = (run: (viewId: string) => void) => () => {
    try {
      run(confirm!.view_id);
    } catch (e) {
      showNotificationGlobal(String(e), "error");
    }
    close();
  };

  return (
    <ConfirmDialog
      open={copy !== null}
      title={copy?.title ?? ""}
      message={copy?.message ?? ""}
      confirmText={copy?.confirmText ?? "Change"}
      cancelText={copy?.cancelText ?? "Cancel"}
      onConfirm={answer((id) => clientApp?.apply_staged_commit(id))}
      onCancel={answer((id) => clientApp?.keep_column_types(id))}
      onClose={close}
    />
  );
};
